import { Injectable, inject, signal } from '@angular/core';
import { ClientApiService } from './client-api.service';
import { CompanyApiService } from '@features/company/company-api.service';
import {
  ClientCompanyRequest,
  ClientCompanyResponse,
  ClientConfigurationRequest,
  ClientConfigurationResponse,
  ClientRequest,
  ClientResponse,
  ClientTransactionHistoryResponse,
} from './client.model';
import { CompanyResponse } from '@features/company/company.model';

@Injectable({ providedIn: 'root' })
export class ClientStore {
  private readonly clientApiService = inject(ClientApiService);
  private readonly companyApiService = inject(CompanyApiService);

  readonly clients = signal<ClientResponse[]>([]);
  readonly history = signal<ClientTransactionHistoryResponse[]>([]);
  readonly clientCompanies = signal<ClientCompanyResponse[]>([]);
  // Every customer (client company) the user can see, for a picker that is not inside one client's page
  readonly allClientCompanies = signal<ClientCompanyResponse[]>([]);
  readonly configurations = signal<ClientConfigurationResponse[]>([]);
  readonly companies = signal<CompanyResponse[]>([]);
  readonly selectedClientId = signal<string | null>(null);
  readonly selectedClient = signal<ClientResponse | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly deactivating = signal(false);
  readonly activating = signal(false);

  private refreshSelectedClient(clientId: string): void {
    this.loadClients({ includeInactive: true });
    if (this.selectedClientId() === clientId) {
      this.selectClient(clientId);
    }
  }

  loadClients({ includeInactive }: { includeInactive: boolean }): void {
    this.loading.set(true);
    this.clientApiService.getClients({ includeInactive }).subscribe({
      next: (clients) => {
        this.clients.set(clients);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  selectClient(clientId: string | null): void {
    this.selectedClientId.set(clientId);
    if (!clientId) {
      this.selectedClient.set(null);
      this.history.set([]);
      this.configurations.set([]);
      this.clientCompanies.set([]);
      return;
    }

    this.loading.set(true);
    this.clientApiService.getClient(clientId).subscribe({
      next: (client) => {
        this.selectedClient.set(client);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });

    this.clientApiService.getClientHistory(clientId).subscribe({
      next: (history) => this.history.set(history),
      error: () => undefined,
    });

    this.clientApiService.getClientConfigurations({ clientId, includeInactive: true }).subscribe({
      next: (configurations) => this.configurations.set(configurations),
      error: () => undefined,
    });

    this.clientApiService.getClientCompanies({ clientId, includeInactive: true }).subscribe({
      next: (clientCompanies) => this.clientCompanies.set(clientCompanies),
      error: () => undefined,
    });

    this.companyApiService.getCompanies({includeInactive: true}).subscribe({
      next: (companies) => this.companies.set(companies),
      error: () => undefined,
    });
  }

  loadAllClientCompanies(): void {
    this.clientApiService.getClientCompanies({}).subscribe({
      next: (clientCompanies) => this.allClientCompanies.set(clientCompanies),
      error: () => undefined,
    });
  }

  filterClientCompanies(clientId?: string | null, companyId?: string | null): void {
    this.clientApiService.getClientCompanies({ clientId, companyId }).subscribe({
      next: (clientCompanies) => this.clientCompanies.set(clientCompanies),
      error: () => undefined,
    });
  }

  createClient(request: ClientRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.clientApiService.createClient(request).subscribe({
      next: () => {
        this.saving.set(false);
        this.loadClients({ includeInactive: true });
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  updateClient(clientId: string, request: ClientRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.clientApiService.updateClient(clientId, request).subscribe({
      next: () => {
        this.saving.set(false);
        this.loadClients({ includeInactive: true });
        if (this.selectedClientId() === clientId) {
          this.selectClient(clientId);
        }
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  deactivateClient(clientId: string, onSuccess: () => void): void {
    this.deactivating.set(true);
    this.clientApiService.deactivateClient(clientId).subscribe({
      next: () => {
        this.deactivating.set(false);
        this.loadClients({ includeInactive: true });
        onSuccess();
      },
      error: () => this.deactivating.set(false),
    });
  }

  activateClient(clientId: string, onSuccess: () => void): void {
    this.activating.set(true);
    this.clientApiService.activateClient(clientId).subscribe({
      next: () => {
        this.activating.set(false);
        this.loadClients({ includeInactive: true });
        onSuccess();
      },
      error: () => this.activating.set(false),
    });
  }

  createConfiguration(request: ClientConfigurationRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.clientApiService.createClientConfiguration(request).subscribe({
      next: () => {
        this.saving.set(false);
        this.refreshSelectedClient(request.clientId);
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  updateConfiguration(configurationId: string, request: ClientConfigurationRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.clientApiService.updateClientConfiguration(configurationId, request).subscribe({
      next: () => {
        this.saving.set(false);
        this.refreshSelectedClient(request.clientId);
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  deactivateConfiguration(configurationId: string, clientId: string, onSuccess: () => void): void {
    this.deactivating.set(true);
    this.clientApiService.deactivateClientConfiguration(configurationId).subscribe({
      next: () => {
        this.deactivating.set(false);
        this.refreshSelectedClient(clientId);
        onSuccess();
      },
      error: () => this.deactivating.set(false),
    });
  }

  activateConfiguration(configurationId: string, clientId: string, onSuccess: () => void): void {
    this.activating.set(true);
    this.clientApiService.activateClientConfiguration(configurationId).subscribe({
      next: () => {
        this.activating.set(false);
        this.refreshSelectedClient(clientId);
        onSuccess();
      },
      error: () => this.activating.set(false),
    });
  }

  createClientCompany(request: ClientCompanyRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.clientApiService.createClientCompany(request).subscribe({
      next: () => {
        this.saving.set(false);
        this.refreshSelectedClient(request.clientId);
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  updateClientCompany(clientCompanyId: string, request: ClientCompanyRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.clientApiService.updateClientCompany(clientCompanyId, request).subscribe({
      next: () => {
        this.saving.set(false);
        this.refreshSelectedClient(request.clientId);
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  deactivateClientCompany(clientCompanyId: string, clientId: string, onSuccess: () => void): void {
    this.deactivating.set(true);
    this.clientApiService.deactivateClientCompany(clientCompanyId).subscribe({
      next: () => {
        this.deactivating.set(false);
        this.refreshSelectedClient(clientId);
        onSuccess();
      },
      error: () => this.deactivating.set(false),
    });
  }

  activateClientCompany(clientCompanyId: string, clientId: string, onSuccess: () => void): void {
    this.activating.set(true);
    this.clientApiService.activateClientCompany(clientCompanyId).subscribe({
      next: () => {
        this.activating.set(false);
        this.refreshSelectedClient(clientId);
        onSuccess();
      },
      error: () => this.activating.set(false),
    });
  }
}
